import { addAttachments } from '@blocksuite/affine-block-attachment';
import { addImages } from '@blocksuite/affine-block-image';
import {
  createStickyNoteAppearance,
  renderStickyNotePaper,
  type StickyNoteColor,
} from '@blocksuite/affine-block-streamlain';
import {
  CanvasElementType,
  EdgelessCRUDIdentifier,
} from '@blocksuite/affine-block-surface';
import {
  FontFamily,
  FontStyle,
  FontWeight,
  MAX_IMAGE_WIDTH,
  type MindmapStyle,
  TextAlign,
} from '@blocksuite/affine-model';
import { TelemetryProvider } from '@blocksuite/affine-shared/services';
import { openSingleFileWith } from '@blocksuite/affine-shared/utils';
import { Bound, type IVec } from '@blocksuite/global/gfx';
import type { BlockComponent } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { html, type TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import * as Y from 'yjs';

export type ConfigProperty = 'x' | 'y' | 'r' | 's' | 'z' | 'o';
export type ConfigState = 'default' | 'active' | 'hover' | 'next';
export type ConfigStyle = Partial<Record<ConfigProperty, number | string>>;
export type ToolConfig = Record<ConfigState, ConfigStyle>;

export type DraggableTool = {
  name: 'text' | 'mindmap' | 'media' | 'sticky' | 'brush-heading';
  icon: TemplateResult;
  preview?: TemplateResult;
  config: ToolConfig;
  standardWidth?: number;
  render: (bound: Bound, edgeless: BlockComponent) => Promise<string | null>;
};

const unitMap = { x: 'px', y: 'px', r: 'deg', s: '', z: '', o: '' };
export const textConfig: ToolConfig = {
  default: { x: -20, y: -8, r: 7.74, s: 0.92, z: 3 },
  active: { x: -22, y: -9, r: -8, s: 0.92 },
  hover: { x: -22, y: -9, r: -8, s: 1, z: 3 },
  next: { x: -22, y: 64, r: 0 },
};
export const mindmapConfig: ToolConfig = {
  default: { x: 4, y: -4, s: 1, r: -7, z: 2 },
  active: { x: 11, y: -14, r: 9, s: 1, z: 2 },
  hover: { x: 11, y: -14, r: 9, s: 1.16, z: 7 },
  next: { y: 64, r: 0 },
};
export const mediaConfig: ToolConfig = {
  default: { x: -20, y: -15, r: 23, s: 1.2, z: 1 },
  active: { x: -25, y: -20, r: -9, s: 1.2 },
  hover: { x: -25, y: -20, r: -9, s: 1.5, z: 3 },
  next: { y: 64, r: 0 },
};
export const stickyConfig: ToolConfig = {
  default: { x: -20, y: -15, r: 0, s: 1.05, z: 1 },
  active: { x: -25, y: -20, r: 0, s: 1.08 },
  hover: { x: -25, y: -20, r: 0, s: 1.25, z: 3 },
  next: { y: 64, r: 0 },
};
export const brushHeadingConfig: ToolConfig = {
  // Upper-right, steeply rising towards the right. The mindmap stays above the
  // resting miniature; only directly hovering the brush brings it forward.
  // Its CSS transform origin pins the top-right position. Active/hover states
  // move that anchor up and right instead of scaling the stroke down-left.
  default: { x: -20, y: -56, r: -58, s: 1.9, z: 1 },
  active: { x: -8, y: -66, r: -58, s: 1.9, z: 1 },
  hover: { x: 4, y: -76, r: -55, s: 1.96, z: 8 },
  next: { y: 64, r: 0 },
};

let stickyPresetId = 0;

export type StickyNotePreset = {
  color: StickyNoteColor;
  paperVariant: number;
  pinOffset: number;
  pinOffsetY: number;
  pinRotation: number;
  rotation: number;
  visualId: string;
};

export function createStickyNotePreset(): StickyNotePreset {
  stickyPresetId += 1;
  return {
    ...createStickyNoteAppearance(),
    visualId: `sticky-note-drag-${stickyPresetId}`,
  };
}

export function renderStickyNoteDragPreview(preset: StickyNotePreset) {
  return html`<div
    class="sticky-note-drag-preview"
    style=${styleMap({
      height: '100%',
      overflow: 'visible',
      position: 'relative',
      transform: `rotate(${preset.rotation}deg)`,
      transformOrigin: 'center',
      width: '100%',
    })}
  >
    ${renderStickyNotePaper({
      ...preset,
      height: '100%',
      position: 'absolute',
      rotation: 0,
      visualId: `${preset.visualId}-drag-preview`,
      width: '100%',
    })}
  </div>`;
}

export function renderStickyNoteToolbarPreview(preset: StickyNotePreset) {
  return renderStickyNotePaper({
    ...preset,
    height: 35.2,
    visualId: `${preset.visualId}-toolbar-preview`,
    width: 43.2,
  });
}

export const stickyRender = async (
  bound: Bound,
  edgeless: BlockComponent,
  preset = createStickyNotePreset()
): Promise<string | null> => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const id = edgeless.store.addBlock(
    'affine:sticky-note',
    {
      text: '',
      color: preset.color,
      assigneeId: '',
      rotate: preset.rotation,
      paperVariant: preset.paperVariant,
      pinOffset: preset.pinOffset,
      pinOffsetY: preset.pinOffsetY,
      pinRotation: preset.pinRotation,
      xywh: new Bound(bound.x, bound.y, bound.w, bound.h).serialize(),
      index: gfx.layer.generateIndex(),
    },
    gfx.surface
  );
  gfx.selection.set({ elements: [id], editing: false });
  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'sticky-note',
  });
  return id;
};

export const brushHeadingRender: DraggableTool['render'] = async (
  bound,
  edgeless
) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const id = edgeless.store.addBlock(
    'affine:brush-heading',
    {
      text: '',
      color: 'beige',
      brushVariant: 0,
      rotate: 0,
      // Preserve the overlay's exact aspect ratio and size on drop. The drag
      // preview and resulting block therefore share geometry as well as SVG.
      xywh: new Bound(bound.x, bound.y, bound.w, bound.h).serialize(),
      index: gfx.layer.generateIndex(),
    },
    gfx.surface
  );
  gfx.selection.set({ elements: [id], editing: false });
  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'brush-heading',
  });
  return id;
};

export const getMindmapRender =
  (mindmapStyle: MindmapStyle): DraggableTool['render'] =>
  async (bound, edgeless) => {
    const [x, y, _, h] = bound.toXYWH();

    const rootW = 145;
    const rootH = 50;

    const nodeW = 80;
    const nodeH = 35;

    const centerVertical = y + h / 2;
    const rootX = x;
    const rootY = centerVertical - rootH / 2;

    type MindMapNode = {
      children: MindMapNode[];
      text: string;
      xywh: string;
    };

    const root: MindMapNode = {
      children: [],
      text: 'Mind Map',
      xywh: `[${rootX},${rootY},${rootW},${rootH}]`,
    };

    for (let i = 0; i < 3; i++) {
      const nodeX = x + rootW + 300;
      const nodeY = centerVertical - nodeH / 2 + (i - 1) * 50;
      root.children.push({
        children: [],
        text: 'Text',
        xywh: `[${nodeX},${nodeY},${nodeW},${nodeH}]`,
      });
    }

    const crud = edgeless.std.get(EdgelessCRUDIdentifier);
    const mindmapId = crud.addElement('mindmap', {
      style: mindmapStyle,
      children: root,
    }) as string;

    edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
      control: 'toolbar:dnd', // for now we use toolbar:dnd for all mindmap creation here
      page: 'whiteboard editor',
      module: 'toolbar',
      segment: 'toolbar',
      type: 'mindmap',
    });

    return mindmapId;
  };

export const textRender: DraggableTool['render'] = async (bound, edgeless) => {
  const gfx = edgeless.std.get(GfxControllerIdentifier);
  const id = edgeless.std
    .get(EdgelessCRUDIdentifier)
    .addElement(CanvasElementType.TEXT, {
      color: '#6E52DF',
      fontFamily: FontFamily.Kalam,
      fontSize: Math.max(16, bound.h),
      fontStyle: FontStyle.Normal,
      fontWeight: FontWeight.SemiBold,
      index: gfx.layer.generateIndex(),
      text: new Y.Text('Text'),
      textAlign: TextAlign.Center,
      xywh: new Bound(bound.x, bound.y, bound.w, bound.h).serialize(),
    }) as string;

  edgeless.store.captureSync();
  gfx.selection.set({ elements: [id], editing: false });

  edgeless.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
    control: 'toolbar:dnd',
    page: 'whiteboard editor',
    module: 'toolbar',
    segment: 'toolbar',
    type: 'text',
  });

  return id;
};

export const mediaRender: DraggableTool['render'] = async (bound, edgeless) => {
  let file: File | null = null;
  try {
    file = await openSingleFileWith();
  } catch (e) {
    console.error(e);
    return null;
  }
  if (!file) return null;

  const files = [file];
  const std = edgeless.std;
  const point: IVec = [bound.x, bound.y];

  // image
  if (file.type.startsWith('image/')) {
    const [id] = await addImages(std, files, {
      point,
      maxWidth: MAX_IMAGE_WIDTH,
      shouldTransformPoint: false,
    });
    return id;
  }

  // attachment
  const [id] = addAttachments(std, files, point, false);
  return id;
};

const toolStyle2StyleObj = (state: ConfigState, style: ConfigStyle = {}) => {
  const styleObj = {} as Record<string, string>;
  for (const [key, value] of Object.entries(style)) {
    styleObj[`--${state}-${key}`] = `${value}${unitMap[key as ConfigProperty]}`;
  }
  return styleObj;
};
export const toolConfig2StyleObj = (config: ToolConfig) => {
  const styleObj = {} as Record<string, string>;
  for (const [state, style] of Object.entries(config)) {
    Object.assign(
      styleObj,
      toolStyle2StyleObj(state as ConfigState, {
        ...config.default,
        ...style,
      })
    );
  }
  return styleObj;
};
